import { type QueryContext } from '@w/db'
import { cachedEntries } from './lexicon-cache-db'
import { type CachedEntry, type Language, type LexiconHit } from './lexicon-model'
import { lexiconTrigrams, normalizeLexiconTrigramText, lexiconPrefixPattern } from './lexicon-trigram'

export type CacheSearchQuery = { language: Language; text: string; limit?: number; threshold?: number }

/** Composition roots select storage and queries together; writes use the source transaction. */
export interface LexiconCacheSearchPort {
  ddl: string
  write(query: QueryContext['query'], id: unknown, entry?: CachedEntry, backfill?: boolean): Promise<void>
  search(query: QueryContext['query'], input: CacheSearchQuery): Promise<CachedEntry[]>
}

// pg_trgm's default similarity threshold (em/requirements.md:35).
export const LEXICON_TRIGRAM_THRESHOLD = 0.3

export const trigramLexiconCacheSearch: LexiconCacheSearchPort = {
  ddl: `CREATE TABLE IF NOT EXISTS lexTrgm (trgm TEXT NOT NULL, entryId BLOB NOT NULL, PRIMARY KEY(trgm, entryId)) WITHOUT ROWID;
CREATE INDEX IF NOT EXISTS idx_lexTrgm_entryId ON lexTrgm(entryId);
CREATE TABLE IF NOT EXISTS lexTrgmDoc (entryId BLOB NOT NULL, n INT NOT NULL, headwordKey TEXT NOT NULL, PRIMARY KEY(entryId));`,
  async write(query, id, entry, backfill = false) {
    // A concurrent replacement/deletion must not be overwritten by a stale backfill.
    const source = backfill && entry ? cachedEntries.entry.encode(entry) : ''
    await query`DELETE FROM lexTrgm WHERE entryId = ${id}
      AND (${backfill ? 1 : 0} = 0 OR EXISTS (SELECT 1 FROM cachedEntries WHERE id = ${id} AND entry = ${source}))`.run()
    await query`DELETE FROM lexTrgmDoc WHERE entryId = ${id}
      AND (${backfill ? 1 : 0} = 0 OR EXISTS (SELECT 1 FROM cachedEntries WHERE id = ${id} AND entry = ${source}))`.run()
    if (!entry) return
    const headwordKey = normalizeLexiconTrigramText(entry.headword).trim()
    const trigrams = lexiconTrigrams(headwordKey)
    // Bounded parameters; postings and count share the source transaction.
    await query`INSERT INTO lexTrgm (trgm, entryId)
      SELECT value, ${id} FROM json_each(${JSON.stringify(trigrams)})
      WHERE ${backfill ? 1 : 0} = 0 OR EXISTS (SELECT 1 FROM cachedEntries WHERE id = ${id} AND entry = ${source})`.run()
    await query`INSERT INTO lexTrgmDoc (entryId, n, headwordKey)
      SELECT ${id}, ${trigrams.length}, ${headwordKey}
      WHERE ${backfill ? 1 : 0} = 0 OR EXISTS (SELECT 1 FROM cachedEntries WHERE id = ${id} AND entry = ${source})`.run()
  },
  async search(query, input) {
    const text = normalizeLexiconTrigramText(input.text).trim()
    const trigrams = lexiconTrigrams(text)
    const limit = input.limit ?? 20
    if (trigrams.length === 0 || limit <= 0) return []
    const rows = await query`
      WITH q(t) AS (SELECT value FROM json_each(${JSON.stringify(trigrams)})),
      scores AS (
        SELECT lexTrgm.entryId, count(*) * 1.0 / (lexTrgmDoc.n + ${trigrams.length} - count(*)) AS sim
        FROM q JOIN lexTrgm ON lexTrgm.trgm = q.t
        JOIN lexTrgmDoc ON lexTrgmDoc.entryId = lexTrgm.entryId
        JOIN cachedEntries ON cachedEntries.id = lexTrgm.entryId
        WHERE cachedEntries.language = ${input.language}
        GROUP BY lexTrgm.entryId
        HAVING count(*) * 1.0 / (lexTrgmDoc.n + ${trigrams.length} - count(*)) >= ${input.threshold ?? LEXICON_TRIGRAM_THRESHOLD}
      )
      SELECT cachedEntries.entry FROM scores
      JOIN cachedEntries ON cachedEntries.id = scores.entryId
      JOIN lexTrgmDoc head ON head.entryId = scores.entryId
      ORDER BY CASE
        WHEN head.headwordKey = ${text} THEN 0
        WHEN head.headwordKey LIKE ${lexiconPrefixPattern(text)} ESCAPE ${'\\'} THEN 1
        ELSE 2 END, sim DESC, cachedEntries.adoptionCount DESC, cachedEntries.headword, cachedEntries.id
      LIMIT ${limit}
    `.execute({ entry: cachedEntries.entry })
    return rows.map(row => row.entry)
  },
}

export function lexiconTrigramMatch(_entry: CachedEntry, _text: string): Pick<LexiconHit, 'matchedField' | 'matchedForm' | 'matchedTranslation'> {
  return { matchedField: 'headword' }
}
